import React, { useEffect, useState } from 'react';
import { TrendingDown, TrendingUp, RefreshCw, Minus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TIER_LABEL = {
  free_trial: 'Free trial',
  essentials: 'Essentials',
  guided: 'Guided',
};

/**
 * Admin → Funnel Snapshot (last 7 days)
 *
 *   signup_started → signup_completed → blueprint_unlocked
 *
 * Conversion rates in Golden Hour #E8A830. WoW trend arrows compare this 7-day
 * window vs the prior 7 days; per-tier breakdown shows free_trial vs essentials
 * vs guided drop-off side-by-side.
 *
 * Data derived from the users collection - no separate analytics events table.
 */

const Trend = ({ deltaPp }) => {
  if (deltaPp === null || deltaPp === undefined) return null;
  if (deltaPp === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-mono uppercase tracking-[0.12em] text-[#0A1F2E]/45"
            data-testid="funnel-trend-flat">
        <Minus className="w-3 h-3" /> flat
      </span>
    );
  }
  const up = deltaPp > 0;
  const Icon = up ? TrendingUp : TrendingDown;
  const color = up ? 'text-[#2CA8A0]' : 'text-[#C4622D]';
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-mono uppercase tracking-[0.12em] ${color}`}
          data-testid={up ? 'funnel-trend-up' : 'funnel-trend-down'}>
      <Icon className="w-3 h-3" />
      {up ? '+' : ''}{deltaPp}pp
    </span>
  );
};

export default function FunnelSnapshotCard() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [tierFilter, setTierFilter] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/funnel-snapshot`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setD(await r.json());
    } catch (e) {
      setErr(e.message || 'Could not load funnel');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const steps = d?.steps || [];
  const trend = d?.trend || {};
  // Map rate index → trend delta for cleaner template
  const rateAt = (i) => {
    if (!d) return null;
    if (i === 0) return { pct: d.rates.overall_pct, delta: trend.overall_delta_pp };
    if (i === 1) return { pct: d.rates.signup_to_complete_pct, delta: trend.signup_to_complete_delta_pp };
    if (i === 2) return { pct: d.rates.complete_to_unlock_pct, delta: trend.complete_to_unlock_delta_pp };
    return null;
  };

  return (
    <section
      data-testid="admin-funnel-snapshot"
      className="bg-white border border-[#F4ECD8] p-6 rounded-md"
    >
      <header className="flex items-end justify-between mb-5">
        <div>
          <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-1">
            Last 7 days · vs prior 7d · derived from users
          </p>
          <h3 className="font-display italic text-2xl text-[#0A1F2E]">Funnel snapshot</h3>
        </div>
        <button onClick={load} disabled={loading} data-testid="funnel-refresh"
          className="text-[#1A5C6E] hover:text-[#2CA8A0] disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </header>

      {err && (
        <p className="font-body text-sm text-red-700 bg-red-50 border-l-4 border-red-400 px-4 py-2 rounded-r mb-4">
          {err}
        </p>
      )}

      <div className="grid grid-cols-3 gap-2 md:gap-4 items-stretch">
        {steps.map((s, i) => {
          const r = rateAt(i);
          return (
            <div key={s.id} className="text-center" data-testid={`funnel-step-${s.id}`}>
              <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 mb-1">{s.label}</p>
              <p className="font-display italic text-4xl text-[#0A1F2E] leading-none">{s.count}</p>
              {r && (
                <div className="mt-2 flex items-center justify-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#E8A830]"
                        data-testid={i === 0 ? 'funnel-overall' : `funnel-rate-${i}`}>
                    {r.pct}% {i === 0 ? 'overall →' : 'converted'}
                  </span>
                  <Trend deltaPp={r.delta} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {d && (
        <div className="mt-5 pt-4 border-t border-[#F4ECD8] grid grid-cols-2 gap-4 text-xs text-[#0A1F2E]/70">
          <p className="flex items-center gap-1.5" data-testid="funnel-dropoff-1">
            <TrendingDown className="w-3.5 h-3.5 text-[#0A1F2E]/40" />
            Drop-off after signup: <span className="font-mono font-medium text-[#0A1F2E]">{d.drop_offs.signup_to_complete_pct}%</span>
          </p>
          <p className="flex items-center gap-1.5" data-testid="funnel-dropoff-2">
            <TrendingDown className="w-3.5 h-3.5 text-[#0A1F2E]/40" />
            Drop-off after onboarding: <span className="font-mono font-medium text-[#0A1F2E]">{d.drop_offs.complete_to_unlock_pct}%</span>
          </p>
        </div>
      )}

      {d?.per_tier && d.per_tier.length > 0 && (
        <div className="mt-5 pt-4 border-t border-[#F4ECD8]" data-testid="funnel-per-tier">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#0A1F2E]/55">
              Drop-off by tier
            </p>
            <div className="flex gap-1.5" data-testid="funnel-tier-filter-chips">
              <button
                onClick={() => setTierFilter(null)}
                data-testid="funnel-tier-chip-all"
                className={`px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.12em] transition
                  ${tierFilter === null
                    ? 'bg-[#0A1F2E] text-[#F9F6EF]'
                    : 'bg-white border border-[#EFE6CF] text-[#0A1F2E] hover:border-[#0A1F2E]/30'}`}
              >
                All
              </button>
              {d.per_tier.map((row) => (
                <button
                  key={row.tier}
                  onClick={() => setTierFilter(row.tier)}
                  data-testid={`funnel-tier-chip-${row.tier}`}
                  className={`px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.12em] transition
                    ${tierFilter === row.tier
                      ? 'bg-[#E8A830] text-[#0A1F2E] font-semibold'
                      : 'bg-white border border-[#EFE6CF] text-[#0A1F2E] hover:border-[#E8A830]'}`}
                >
                  {TIER_LABEL[row.tier] || row.tier}
                </button>
              ))}
            </div>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[#0A1F2E]/55 font-mono uppercase text-[9px] tracking-[0.12em]">
                <th className="pb-2">Tier</th>
                <th className="pb-2 text-right">Started</th>
                <th className="pb-2 text-right">Completed</th>
                <th className="pb-2 text-right">Unlocked</th>
                <th className="pb-2 text-right">Drop-off after signup</th>
                <th className="pb-2 text-right">Drop-off after onboarding</th>
              </tr>
            </thead>
            <tbody>
              {d.per_tier
                .filter((row) => !tierFilter || row.tier === tierFilter)
                .map((row) => (
                <tr key={row.tier} className="border-t border-[#F4ECD8]/60" data-testid={`funnel-tier-${row.tier}`}>
                  <td className="py-2 font-body text-[#0A1F2E]">{TIER_LABEL[row.tier] || row.tier}</td>
                  <td className="py-2 text-right font-mono text-[#0A1F2E]/75">{row.started}</td>
                  <td className="py-2 text-right font-mono text-[#0A1F2E]/75">{row.completed}</td>
                  <td className="py-2 text-right font-mono text-[#0A1F2E]/75">{row.unlocked}</td>
                  <td className="py-2 text-right font-mono text-[#0A1F2E]">{row.dropoff_after_signup_pct}%</td>
                  <td className="py-2 text-right font-mono text-[#0A1F2E]">{row.dropoff_after_onboarding_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
